import { useEffect } from 'react';
import { useReportClientEvent } from './ClientEventContext';
import { classifyNativeError, nativeErrorBrandsFor } from './nativeErrorBrand';
import { UNHANDLED_EXCEPTION_EVENT_TYPE } from './clientTelemetryContract';
// Same closed set as `render_failure` (architect ruling, `decisions/architect/
// 2026-09-01-client-error-event-type-set-contract-doc-ruling.md`) — both producers
// classify a native-or-unknown thrown/rejected value through the SAME rule, so the
// brand list and the classifier are shared (`nativeErrorBrand.ts`), not restated.
// Exported so a test can compare it against the contract's own declared set
// (architect review, web!632 F1) — the generator's `assertSameSet` checks never
// cover `error_name`, so this member list is pinned by nothing else.
export const NATIVE_BRAND_NAMES = [
    'AggregateError',
    'EvalError',
    'RangeError',
    'ReferenceError',
    'SuppressedError',
    'SyntaxError',
    'TypeError',
    'URIError',
    'Error',
];
const NATIVE_BRANDS = nativeErrorBrandsFor(NATIVE_BRAND_NAMES);
export function unhandledExceptionErrorName(error) {
    return classifyNativeError(error, NATIVE_BRANDS);
}
/**
 * Installs the two global listeners and returns an unsubscribe function — the same
 * install/unsubscribe shape as `tapRouterRenderFailures`, so a shell can re-mount the
 * reporter without leaking a prior pair.
 *
 * ⛔ NO CONTEXT.ROUTE, deliberately, for v1. Unlike `RenderFailureReporter` (which
 * resolves route attribution from `useMatches()` inside the render tree it wraps), a
 * global `window` listener fires OUTSIDE any component's render — there is no route
 * to read at the call site. The blocker is NOT cost: `RouteViewReporter` already runs
 * an always-on `useMatches()` subscription as this component's sibling in `routes.tsx`,
 * so caching "current route" from it would be a handful of lines, not a second
 * subscription. The real blocker is cross-repo sequencing: `context.route` is a closed
 * vocabulary asserted subset-of gateway's `clientTelemetryContextValueAllowlist["route"]`
 * (`gen-client-telemetry.mjs`'s `assertSameSet(dimensions.routes, contract.context.route)`),
 * so adding it to a new event type needs `api`'s allowlist table extended first — the
 * same type-by-type dependency the contract-doc ruling's D2.5 describes for the other
 * remaining types. Inventing an approximate route from `location.pathname` instead
 * would not be honest either way — the whole codebase's rule is route attribution ONLY
 * from matched route-definition metadata, never from user-controlled path text.
 *
 * ⛔ NO CLIENT-SIDE DEDUP. `render_failure`'s router tap needs failure-epoch memory
 * because the SAME retained router error re-notifies on every subscriber tick; a
 * `window` `error`/`unhandledrejection` event fires exactly once per genuine
 * occurrence, so there is no retained-value re-notification to suppress. The volume
 * concern the proposal flagged (a render loop throwing hundreds of times per second)
 * is the per-type BUDGET's job (`CLIENT_EVENT_BUDGET.unhandled_exception`,
 * `clientEvent.ts`), not a second, redundant client-side limiter — but the budget
 * bounds VOLUME, not informativeness: a cross-origin third-party script with no
 * `crossorigin` attribute (e.g. `apps/mini-app/index.html`'s Telegram web-app SDK)
 * reports its uncaught throws as an opaque `"Script error."` with `event.error ===
 * null`, classified `unknown` here and reported unconditionally. On that shell, such
 * a script consuming the whole per-type budget before a genuinely diagnostic
 * exception fires is a real, unresolved case — not a reason to add a second limiter,
 * but not one the budget alone rules out either.
 */
export function installUnhandledExceptionReporter(report, target = globalThis) {
    const onError = (event) => {
        report(unhandledExceptionErrorName(event.error));
    };
    const onRejection = (event) => {
        report(unhandledExceptionErrorName(event.reason));
    };
    target.addEventListener('error', onError);
    target.addEventListener('unhandledrejection', onRejection);
    return () => {
        target.removeEventListener('error', onError);
        target.removeEventListener('unhandledrejection', onRejection);
    };
}
/**
 * Mounted ONCE, as a sibling of `RouteViewReporter` at the shared route table's root
 * (`routes.tsx`) — every shell that imports `routes` gets the tap without per-shell
 * wiring, same reasoning as `RenderFailureReporter`. Renders nothing; registers on
 * mount, unsubscribes on unmount (empty-if-report-identity-stable deps — `report`
 * from `useReportClientEvent()` is stable for the life of the provider).
 *
 * ⚠️ This component is INSIDE `RenderFailureReporter`'s boundary in `layoutRoot`.
 * `RenderFailureBoundary.componentDidCatch` reports-and-RETHROWS unconditionally
 * (never renders a fallback), and `routes.tsx` declares no `errorElement`, so an
 * escaped render failure unmounts `layoutRoot` — including this component, whose
 * cleanup then removes both `window` listeners. `unhandled_exception` reporting is
 * therefore OFF for the remainder of the session after any render failure escapes to
 * the router's default error boundary. This is the same placement `RouteViewReporter`
 * already has; moving this component higher (a shell-level provider, outside the
 * router) would trade away the no-per-shell-wiring property that is this seam's whole
 * point, so the tradeoff is documented here rather than resolved.
 */
export function UnhandledExceptionReporter() {
    const report = useReportClientEvent();
    useEffect(() => installUnhandledExceptionReporter((errorName) => {
        report({ eventType: UNHANDLED_EXCEPTION_EVENT_TYPE, errorName });
    }), [report]);
    return null;
}
//# sourceMappingURL=unhandledExceptionReporter.js.map